import type { ChangeHistory } from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  getVisibleNodeNames,
  type ViewQueryField,
  type ViewQueryList,
  type ViewQueryNode,
  type ViewQueryNodeReadJoinOnId,
  type ViewQueryRecordNode,
  type With,
} from '@salescore/core'
import { Array, pipe, Result } from 'effect'

import { type DeepReadonly, toMutable } from '../../rsheet/types/DeepReadonly'
import { replaceChildRecordNode } from '../../rsheet/util/replaceChildRecordNode'
import { getInnerRecordNodePure, getRecordNodeFromRootNodePure } from '../../state/nodeUtility'

export interface MagnifyingGlassChange {
  rowIndex: number
  innerRowIndex: number
  node: ViewQueryNode
  fetchedChildNode: With<ViewQueryRecordNode, 'id'>
  parentReferenceField: ViewQueryField | undefined
  parentNode: ViewQueryNode
  joinOnId: ViewQueryNodeReadJoinOnId
}

// MagnifyingGlassChange を records に適用する
// インプット検査・本処理・アウトプット値形成を含む
export const applyMagnifyingGlassChange = (
  records: DeepReadonly<ViewQueryRecordNode[]>,
  query: DeepReadonly<Pick<ViewQueryList, 'fields'>>,
  {
    node,
    rowIndex,
    innerRowIndex,
    fetchedChildNode,
    parentReferenceField,
    parentNode,
    joinOnId,
  }: MagnifyingGlassChange,
): Result.Result<
  | {
      records: DeepReadonly<ViewQueryRecordNode[]>
      change: ChangeHistory
      referencingNode: ViewQueryRecordNode
      referencedNode: ViewQueryRecordNode
    }
  | 'referenceAlreadyUptoDate',
  { message: string } | undefined
> => {
  // インプット検査
  if (parentNode.write === undefined) {
    return Result.fail({ message: `parentNode.write is undefined. parentNode.name = ${parentNode.name}` })
  }

  const parentAndRootNodesEither = getParentAndRootNodes({
    records,
    parentNodePath: parentNode.path,
    rowIndex,
    innerRowIndex,
  })

  if (Result.isFailure(parentAndRootNodesEither)) {
    return Result.fail(parentAndRootNodesEither.failure)
  }
  const { rootRecordNode, parentRecordNode } = parentAndRootNodesEither.success

  const currentReferenceId =
    parentReferenceField === undefined ? undefined : parentRecordNode.attributes[parentReferenceField.name]

  // すでに親ノードから子ノードへ、すでに期待する参照が設定されている場合は何もしない
  if (parentReferenceField !== undefined && currentReferenceId === fetchedChildNode.id) {
    return Result.succeed('referenceAlreadyUptoDate')
  }

  // 本処理
  const newRecordsEither = _applyMagnifyingGlassChange({
    records,
    rootRecordNode,
    rowIndex,
    node,
    parentNode,
    parentReferenceField,
    parentRecordNode,
    fetchedChildNode,
    visibleNodeNames: getVisibleNodeNames(query),
  })
  if (Result.isFailure(newRecordsEither)) {
    return Result.fail(newRecordsEither.failure)
  }

  // アウトプット値形成
  const newRecords = newRecordsEither.success
  const newParentAndRootNodesEither = getParentAndRootNodes({
    records: newRecords,
    parentNodePath: parentNode.path,
    rowIndex,
    innerRowIndex,
  })
  if (Result.isFailure(newParentAndRootNodesEither)) {
    return Result.fail(newParentAndRootNodesEither.failure)
  }
  const { parentRecordNode: newParentRecordNode } = newParentAndRootNodesEither.success
  const newChange: ChangeHistory = {
    datetime: new Date().toISOString(),
    // eslint-disable-next-line sonarjs/todo-tag
    rowIndices: [0], // TODO: 結局使ってないので廃止
    snapshot: JSON.stringify(records),
    snapshotAfterChange: JSON.stringify(newRecords),
    changes: [
      {
        rootNodeId: rootRecordNode.id,
        id: newParentRecordNode.id,
        streamName: parentNode.write.streamName,
        fieldChanges: [], // callbackは現状の仕様だと特にないはず
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: ここのchangesのロジックだけpropertySourceNameではなくpropertyNameになっている。後者に統一したい。
        before: {
          [joinOnId.meta.parentNodePropertyName]: currentReferenceId,
        },
        after: {
          [joinOnId.meta.parentNodePropertyName]: fetchedChildNode.id,
        },
      },
    ],
  }

  return Result.succeed({
    records: newRecords,
    change: newChange,
    referencingNode: newParentRecordNode,
    referencedNode: fetchedChildNode,
  })
}

// 本処理
const _applyMagnifyingGlassChange = ({
  records,
  rootRecordNode,
  rowIndex,
  node,
  parentNode,
  parentReferenceField,
  parentRecordNode,
  fetchedChildNode,
  visibleNodeNames,
}: {
  records: DeepReadonly<ViewQueryRecordNode[]>
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>
  rowIndex: number
  node: ViewQueryNode
  parentNode: ViewQueryNode
  parentReferenceField: ViewQueryField | undefined
  parentRecordNode: ViewQueryRecordNode
  fetchedChildNode: With<ViewQueryRecordNode, 'id'>
  visibleNodeNames: ReturnType<typeof getVisibleNodeNames>
}): Result.Result<DeepReadonly<ViewQueryRecordNode[]>, undefined> =>
  pipe(
    updateChildTableNode(parentRecordNode, node, fetchedChildNode),
    (parentRecordNode) => updateParentReferenceField(parentRecordNode, parentReferenceField, fetchedChildNode),
    (parentRecordNode) =>
      parentRecordNode.id === rootRecordNode.id
        ? Result.succeed(parentRecordNode)
        : replaceChildRecordNode(rootRecordNode, parentNode.path, parentRecordNode),
    Result.map((record) => assignInnerRowGeometry(toMutable(record), visibleNodeNames)),
    Result.map((rootRecordNode) => records.toSpliced(rowIndex, 1, rootRecordNode)),
  )

function getParentAndRootNodes({
  records,
  parentNodePath,
  rowIndex,
  innerRowIndex,
}: {
  records: DeepReadonly<ViewQueryRecordNode[]>
  parentNodePath: string[]
  rowIndex: number
  innerRowIndex: number
}): Result.Result<
  {
    rootRecordNode: DeepReadonly<ViewQueryRecordNode>
    parentRecordNode: ViewQueryRecordNode
  },
  { message: string }
> {
  const rootRecordNode = records[rowIndex]
  if (rootRecordNode === undefined) {
    return Result.fail({ message: `rootRecordNode not found in resolveParentRecordNode. rowIndex=${rowIndex}` })
  }

  const parentRecordNodes = getRecordNodeFromRootNodePure(rootRecordNode, parentNodePath)
  const parentRecordNode = Array.isArray(parentRecordNodes)
    ? getInnerRecordNodePure(parentRecordNodes, innerRowIndex)
    : parentRecordNodes

  if (parentRecordNode === undefined) {
    return Result.fail({
      message: `parentRecordNode not found in resolveParentRecordNode. parentNodePath=${parentNodePath.join(',')}`,
    })
  }

  return Result.succeed({ rootRecordNode, parentRecordNode })
}

const updateChildTableNode = (
  parentRecordNode: ViewQueryRecordNode,
  node: ViewQueryNode,
  fetchedChildNode: With<ViewQueryRecordNode, 'id'>,
): ViewQueryRecordNode => {
  const targetTableNode = parentRecordNode.children.find((x) => x.nodeName === node.name)

  if (targetTableNode === undefined) {
    return {
      ...parentRecordNode,
      children: [
        ...parentRecordNode.children,
        {
          nodeName: node.name,
          children: [fetchedChildNode],
        },
      ],
    }
  }

  if (!targetTableNode.children.some((x) => x.id === fetchedChildNode.id)) {
    return {
      ...parentRecordNode,
      children: parentRecordNode.children.map((child) =>
        child.nodeName === node.name // targetTableNode か否か
          ? {
              ...child,
              children: [fetchedChildNode], // 既存の子レコードを fetchedChildNode に置き換える
            }
          : child,
      ),
    }
  }

  // 既に fetchedChildNode がテーブルノードに存在する場合は何もしない
  return parentRecordNode
}

const updateParentReferenceField = (
  parentRecordNode: ViewQueryRecordNode,
  parentReferenceField: ViewQueryField | undefined,
  fetchedChildNode: With<ViewQueryRecordNode, 'id'>,
): ViewQueryRecordNode => {
  // 親ノードに、子ノードを参照するフィールドがない場合は何もしない
  if (parentReferenceField === undefined) {
    return parentRecordNode
  }

  return {
    ...parentRecordNode,
    attributes: {
      ...parentRecordNode.attributes,
      [parentReferenceField.name]: fetchedChildNode.id,
      [`${parentReferenceField.name}_label`]: fetchedChildNode.attributes[`${parentReferenceField.name}_label`],
    },
  }
}
